import React, { ComponentType, useEffect } from 'react';
import { TFunction } from 'react-i18next';
import { Outlet } from '@tanstack/react-router';
import { connect } from 'react-redux';
import {
  MenuSidebar,
  SplitView,
  Root,
  DataRetentionBanner,
  withTranslation,
  IconName,
  SessionTimeoutModal,
  PaymentFailedBanner,
  DomainsCertificateExpirationModal,
  JWTProvider,
  LicenseExpirationGuard,
  CapGuardBanner,
} from '@piwikpro/ui-components';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';
import { IAuthSettingsState } from '@piwikpro/auth-crate/reducers/auth';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { AuthSettings, AuthSettingsResponse } from '@piwikpro/auth-crate';
import { Session } from '@piwikpro/auth-crate/Session';
import { Notifications } from '@piwikpro/notification-crate';
import { OrganizationState } from '@piwikpro/organization-crate';
import { ReactRouterLink } from '@piwikpro/router-crate/components';

export interface IUserPanelProps {
  authService: AuthSettings
  sessionService: Session
  userId: SessionState['userId']
  jwt: SessionState['jwt']
  ProductBar: ComponentType<any>
  Title: ComponentType<any>
  isSessionLoading: SessionState['isLoading']
  userAuthSettings: AuthSettingsResponse
  sessionExpiration: SessionState['sessionExpiration']
  t: TFunction
  notifications: Notifications
}

export const UserPanel = ({
  authService,
  userId,
  jwt,
  isSessionLoading,
  ProductBar,
  Title,
  userAuthSettings,
  sessionService,
  sessionExpiration,
  t,
  notifications,
}: IUserPanelProps) => {
  const fetchUserSettings = async () => {
    try {
      await authService.getSettingsForUser(userId!);
      // eslint-disable-next-line no-empty
    } catch {}
  };

  useEffect(() => {
    fetchUserSettings();
  }, []);

  const menuSidebarConfig = {
    listData: [
      {
        section: '',
        links: [
          {
            text: t('profile.title'),
            icon: 'user' as IconName,
            exact: true,
            navigationPath: '/profile',
          },
          ...userAuthSettings.saml?.enabled ? [] : [{
            text: t('security.title'),
            icon: 'shield' as IconName,
            navigationPath: '/profile/security',
          }],
          {
            text: t('credentials.title'),
            icon: 'key' as IconName,
            navigationPath: '/profile/api-credentials',
          },
        ],
      },
    ],
    routerLink: ReactRouterLink,
  };

  return (
    <JWTProvider jwt={jwt!} refreshJWT={sessionService.refreshJWT}>
      <Root title={null} withThemes>
        <Root.TopSection>
          <ProductBar>{t('moduleName')}</ProductBar>
          <DataRetentionBanner
            onContactFormSubmitSuccess={({ message }) => {
              notifications.push({
                text: message,
                type: 'success',
                autoCloseSeconds: 5,
              });
            }}
            onContactFormSubmitFail={({ message }) => {
              notifications.push({
                text: message,
                type: 'error',
                autoCloseSeconds: 5,
              });
            }}
          />
          <SessionTimeoutModal
            sessionExpiration={sessionExpiration}
            onLogout={() => sessionService.logout()}
            onUpdateToken={() => sessionService.getSession().catch(() => {
              sessionService.logout();
            })}
            updateTokenPending={isSessionLoading}
          />
          <DomainsCertificateExpirationModal />
          <LicenseExpirationGuard onLogout={() => sessionService.logout()} />
          <PaymentFailedBanner />
          <CapGuardBanner />
        </Root.TopSection>
        <Root.Content>
          <Title part={t('moduleName')}>
            <SplitView
              leftSidebar={{
                sidebar: <MenuSidebar routerMode="v7" {...menuSidebarConfig} />,
              }}
            >
              <Outlet />
            </SplitView>
          </Title>
        </Root.Content>
      </Root>
    </JWTProvider>
  );
};

const mapStateToProps = (state: {
  session: SessionState
  organization: OrganizationState
  authSettings: IAuthSettingsState
}) => ({
  jwt: state.session.jwt,
  isSessionLoading: state.session.isLoading,
  userId: state.session.userId,
  userAuthSettings: state.authSettings.user,
  sessionExpiration: state.session.sessionExpiration,
  isTrial: state.organization.details.license?.current?.plan.id === 'trial',
  trialExpiresAt: state.organization.details.license?.current?.expires_at,
});

export default withInject({
  ProductBar: 'AuthCrate.components.ProductBar',
  Title: 'TitleCrate.components.Title',
  authService: 'AuthCrate.authSettings',
  sessionService: 'AuthCrate.session',
  notifications: 'NotificationCrate.notifications',
})(
  connect(mapStateToProps, null)((
    withTranslation(['profile'])(
      errorBoundary(UserPanel),
    )) as any),
);
